// Case Details Dialog Component - Displays case information in a slide drawer
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type CollectionCase, type CaseNote, type CaseAuditLog } from "@/types/collections";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  FileText,
  Activity,
  CreditCard,
  MessageSquare,
  Pencil,
  Save,
  X,
  History,
  ArrowRightLeft,
  Plus,
  Minus,
  Edit,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { MarkPromiseFulfilledModal, MarkPromiseBrokenModal } from "./action-modals";
import { CompleteActionDialog, type CompleteActionData } from "./complete-action-dialog";
import { SendFinalNoticeDialog } from "./SendFinalNoticeDialog";
import { ConsolidatedActivityTimeline } from "./consolidated-activity-timeline";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { useUsersList } from "@/hooks/useUsersData";
import { formatCurrency } from "@/lib/utils/formatters";

interface CaseDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseData: CollectionCase | null;
}

export function CaseDetailsDialog({ open, onOpenChange, caseData }: CaseDetailsDialogProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<CollectionCase | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [notesHistory, setNotesHistory] = useState<CaseNote[]>([]);
  const [showMarkFulfilledModal, setShowMarkFulfilledModal] = useState(false);
  const [showMarkBrokenModal, setShowMarkBrokenModal] = useState(false);
  const [showCompleteActionDialog, setShowCompleteActionDialog] = useState(false);
  const [showSendFinalNoticeDialog, setShowSendFinalNoticeDialog] = useState(false);

  // Fetch active users for the Assigned To dropdown
  const { data: users = [] } = useUsersList();
  const activeUsers = users.filter(user => user.status === 'active');

  // Initialize edited data when caseData changes
  if (!caseData) return null;

  // Reset edit mode when dialog opens/closes or case changes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsEditMode(false);
      setEditedData(null);
      setSelectedTransactions(new Set());
    }
    onOpenChange(open);
  };

  const handleEditClick = () => {
    setEditedData({ ...caseData });
    setIsEditMode(true);
    // Initialize selected transactions with all transactions by default
    setSelectedTransactions(new Set(mockTransactions.map(t => t.id)));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData(null);
    setSelectedTransactions(new Set());
  };

  const handleToggleTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSaveEdit = () => {
    if (!editedData) return;

    // Validate required fields
    if (!editedData.customerEmail || !editedData.customerPhone) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedData.customerEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Get selected transaction details for logging/API call
    const selectedTransactionsList = Array.from(selectedTransactions);
    console.log("Selected transactions:", selectedTransactionsList);
    console.log("Total selected:", selectedTransactionsList.length);

    // Here you would typically make an API call to save the changes
    // including the selected transactions
    // For now, we'll just show a success message
    const message = selectedTransactionsList.length > 0 
      ? `Case updated successfully with ${selectedTransactionsList.length} transaction(s) selected`
      : "Case updated successfully";
    
    toast.success(message);
    setIsEditMode(false);
    setEditedData(null);
    setSelectedTransactions(new Set());

    // In a real implementation, you would update the parent component's state
    // or refetch the data from the API
  };

  const currentData = isEditMode && editedData ? editedData : caseData;

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Promised":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "Escalated":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  // Mock data for additional details
  const mockTransactions = [
    {
      id: "1",
      invoiceNumber: "INV-2024-001",
      invoiceDate: new Date(2024, 0, 15),
      dueDate: new Date(2024, 1, 15),
      type: "invoice" as const,
      amount: caseData.amount * 0.4,
      balance: caseData.amount * 0.4,
      status: "Overdue",
      daysOverdue: caseData.daysInCollection,
    },
    {
      id: "2",
      invoiceNumber: "INV-2024-002",
      invoiceDate: new Date(2024, 1, 1),
      dueDate: new Date(2024, 2, 1),
      type: "invoice" as const,
      amount: caseData.amount * 0.35,
      balance: caseData.amount * 0.35,
      status: "Overdue",
      daysOverdue: Math.max(0, caseData.daysInCollection - 15),
    },
    {
      id: "3",
      invoiceNumber: "INV-2024-003",
      invoiceDate: new Date(2024, 2, 10),
      dueDate: new Date(2024, 3, 10),
      type: "credit_note" as const,
      amount: caseData.amount * 0.25,
      balance: caseData.amount * 0.25,
      status: "Outstanding",
      daysOverdue: 0,
    },
  ];

  const mockActivities = [
    {
      id: "1",
      caseId: caseData.caseId,
      type: "call" as const,
      description: "Attempted phone call - no answer",
      outcome: "Left voicemail requesting callback",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "2",
      caseId: caseData.caseId,
      type: "email" as const,
      description: "Sent payment reminder email",
      outcome: "Email delivered successfully",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 9, 15),
    },
    {
      id: "3",
      caseId: caseData.caseId,
      type: "note" as const,
      description: "Customer requested payment plan options",
      outcome: "Sent payment plan proposal",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 11, 45),
    },
    {
      id: "4",
      caseId: caseData.caseId,
      type: "call" as const,
      description: "Spoke with accounts payable manager",
      outcome: "Promised payment by end of week",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 12, 16, 20),
    },
  ];

  const mockCommunications = [
    {
      id: "1",
      caseId: caseData.caseId,
      type: "email" as const,
      direction: "outbound" as const,
      subject: "Payment Reminder - Account Overdue",
      content: "Dear Customer, This is a friendly reminder that your account has an outstanding balance...",
      sentAt: new Date(2024, 2, 18, 9, 15),
      sentBy: "Sarah Johnson",
      status: "delivered" as const,
    },
    {
      id: "2",
      caseId: caseData.caseId,
      type: "email" as const,
      direction: "inbound" as const,
      subject: "RE: Payment Reminder",
      content: "Thank you for your email. We are experiencing temporary cash flow issues...",
      sentAt: new Date(2024, 2, 16, 14, 30),
      sentBy: caseData.customerName,
      status: "delivered" as const,
    },
  ];

  // Mock notes history data - Initialize with some sample notes
  const mockNotesHistory: CaseNote[] = [
    {
      id: "1",
      caseId: caseData.caseId,
      content: "Customer called to discuss payment options. Mentioned they are waiting for a large client payment expected next week. Agreed to follow up on March 28th.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 21, 10, 30),
    },
    {
      id: "2",
      caseId: caseData.caseId,
      content: "Sent detailed payment plan proposal via email. Plan includes 3 monthly installments of $5,000 each. Awaiting customer response.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 19, 14, 15),
    },
    {
      id: "3",
      caseId: caseData.caseId,
      content: "Initial contact made. Customer acknowledged the outstanding balance and requested time to review their cash flow situation. Scheduled follow-up call for next week.",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 9, 45),
    },
    {
      id: "4",
      caseId: caseData.caseId,
      content: "Case assigned to collections team. Customer has been unresponsive to previous payment reminders. Priority set to High due to amount and age of debt.",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10, 8, 0),
    },
  ];

  // Initialize notes history state with mock data if not already set
  if (notesHistory.length === 0 && open) {
    setNotesHistory(mockNotesHistory);
  }



  const getActivityIcon = (type: string) => {
    switch (type) {
      case "call":
        return Phone;
      case "email":
        return Mail;
      case "note":
        return FileText;
      case "payment":
        return CreditCard;
      default:
        return Activity;
    }
  };

  // Mock audit log data - comprehensive case modification history
  const mockAuditLogs: CaseAuditLog[] = [
    {
      id: "audit-1",
      caseId: caseData.caseId,
      action: "status_change",
      description: "Case status changed",
      fieldName: "status",
      oldValue: "On Hold",
      newValue: "Active",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 22, 9, 15),
    },
    {
      id: "audit-2",
      caseId: caseData.caseId,
      action: "priority_change",
      description: "Priority level updated",
      fieldName: "priority",
      oldValue: "Medium",
      newValue: "High",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "audit-3",
      caseId: caseData.caseId,
      action: "transaction_added",
      description: "Transaction added to case",
      transactionId: "3",
      transactionNumber: "INV-2024-003",
      transactionAmount: caseData.amount * 0.25,
      transactionDate: new Date(2024, 2, 10),
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 19, 11, 20),
    },
    {
      id: "audit-4",
      caseId: caseData.caseId,
      action: "field_update",
      description: "Customer contact information updated",
      fieldName: "customerEmail",
      oldValue: "old.email@example.com",
      newValue: caseData.customerEmail,
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 16, 45),
    },
    {
      id: "audit-5",
      caseId: caseData.caseId,
      action: "assignment_change",
      description: "Case reassigned to different collector",
      fieldName: "assignedTo",
      oldValue: "John Smith",
      newValue: "Sarah Johnson",
      createdBy: "System",
      createdAt: new Date(2024, 2, 15, 8, 30),
    },
    {
      id: "audit-6",
      caseId: caseData.caseId,
      action: "status_change",
      description: "Case status changed",
      fieldName: "status",
      oldValue: "Active",
      newValue: "On Hold",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 12, 13, 10),
    },
    {
      id: "audit-7",
      caseId: caseData.caseId,
      action: "transaction_removed",
      description: "Transaction removed from case",
      transactionId: "4",
      transactionNumber: "INV-2024-004",
      transactionAmount: 2500,
      transactionDate: new Date(2024, 1, 5),
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 11, 10, 15),
    },
    {
      id: "audit-8",
      caseId: caseData.caseId,
      action: "transaction_added",
      description: "Transaction added to case",
      transactionId: "2",
      transactionNumber: "INV-2024-002",
      transactionAmount: caseData.amount * 0.35,
      transactionDate: new Date(2024, 1, 1),
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 10, 14, 0),
    },
    {
      id: "audit-9",
      caseId: caseData.caseId,
      action: "transaction_added",
      description: "Transaction added to case",
      transactionId: "1",
      transactionNumber: "INV-2024-001",
      transactionAmount: caseData.amount * 0.4,
      transactionDate: new Date(2024, 0, 15),
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 10, 13, 45),
    },
    {
      id: "audit-10",
      caseId: caseData.caseId,
      action: "case_created",
      description: "Collection case created",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10, 8, 0),
    },
  ];

  const getAuditLogIcon = (action: CaseAuditLog['action']) => {
    switch (action) {
      case "status_change":
        return ArrowRightLeft;
      case "transaction_added":
        return Plus;
      case "transaction_removed":
        return Minus;
      case "priority_change":
        return AlertTriangle;
      case "assignment_change":
        return UserPlus;
      case "field_update":
        return Edit;
      case "note_added":
        return FileText;
      case "case_created":
        return History;
      default:
        return Activity;
    }
  };

  const getAuditLogColor = (action: CaseAuditLog['action']) => {
    switch (action) {
      case "status_change":
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400";
      case "transaction_added":
        return "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400";
      case "transaction_removed":
        return "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400";
      case "priority_change":
        return "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400";
      case "assignment_change":
        return "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400";
      case "field_update":
        return "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400";
      case "note_added":
        return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400";
      case "case_created":
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
      default:
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
    }
  };

  // Handle completing the current action
  const handleCompleteAction = (data: CompleteActionData) => {
    console.log("Action completed:", data);
    
    // In a real implementation, this would:
    // 1. Update the case with the completion data
    // 2. Add the notes to the case history
    // 3. Set the next action and date if provided
    // 4. Update the case status if required
    // 5. Refresh the case data
    
    // For now, we'll just show a success message
    toast.success("Action completed successfully", {
      description: data.nextAction 
        ? `Next action scheduled: ${data.nextAction}` 
        : "No next action scheduled"
    });
    
    // Close the dialog
    setShowCompleteActionDialog(false);
    
    // In a real app, you would refresh the case data here
    // or update the parent component's state
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <div className="flex items-start justify-between pr-8">
            <div>
              <SheetTitle className="text-2xl">Case Details</SheetTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Complete information for case {caseData.caseId}
              </p>
            </div>
            {!isEditMode ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditClick}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-4 mx-6 mt-4 flex-shrink-0">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 mt-4">
            <ScrollAreaViewport className="h-full w-full px-6 pb-6">
            <TabsContent value="overview" className="space-y-6">
              {/* Two Column Layout - Customer & Case Info */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Customer Information & Contact */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Customer Information
                    </CardTitle>
                    <CardDescription>Contact details and customer profile</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      {/* Amendment 1: Consolidated Customer field showing ID and Name together */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Customer</p>
                        <p className="text-lg font-semibold">{currentData.customerId} {currentData.customerName}</p>
                      </div>
                      
                      <Separator />

                      <div className="space-y-1">
                        <Label htmlFor="email" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          Email Address
                        </Label>
                        {isEditMode && editedData ? (
                          <Input
                            id="email"
                            type="email"
                            value={editedData.customerEmail}
                            onChange={(e) => setEditedData({ ...editedData, customerEmail: e.target.value })}
                            placeholder="customer@example.com"
                          />
                        ) : (
                          <div className="flex items-center justify-between group">
                            <a 
                              href={`mailto:${currentData.customerEmail}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {currentData.customerEmail}
                            </a>
                            <Button size="sm" variant="ghost" className="h-8 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Mail className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="phone" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Phone Number
                        </Label>
                        {isEditMode && editedData ? (
                          <Input
                            id="phone"
                            type="tel"
                            value={editedData.customerPhone}
                            onChange={(e) => setEditedData({ ...editedData, customerPhone: e.target.value })}
                            placeholder="(555) 123-4567"
                          />
                        ) : (
                          <div className="flex items-center justify-between group">
                            <a 
                              href={`tel:${currentData.customerPhone}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {currentData.customerPhone}
                            </a>
                            <Button size="sm" variant="ghost" className="h-8 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Phone className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Case Management */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Case Management
                    </CardTitle>
                    <CardDescription>Case details and configuration</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* First Column */}
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Case ID</p>
                          <p className="text-sm font-mono font-semibold">{currentData.caseId}</p>
                        </div>

                        <Separator />

                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Collection Strategy</p>
                          <p className="text-sm font-semibold">{currentData.strategy.name}</p>
                          <p className="text-xs text-muted-foreground">{currentData.strategy.type}</p>
                        </div>

                        <Separator />

                        <div className="space-y-1">
                          <Label htmlFor="assignedTo" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <UserPlus className="h-3 w-3" />
                            Assigned User
                          </Label>
                          {isEditMode && editedData ? (
                            <Select
                              value={editedData.assignedTo || "unassigned"}
                              onValueChange={(value) => 
                                setEditedData({ ...editedData, assignedTo: value === "unassigned" ? undefined : value })
                              }
                            >
                              <SelectTrigger id="assignedTo">
                                <SelectValue placeholder="Select user..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                {activeUsers.map((user) => (
                                  <SelectItem key={user.id} value={`${user.firstName} ${user.lastName}`}>
                                    {user.firstName} {user.lastName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="text-sm">
                              {currentData.assignedTo || "Unassigned"}
                            </p>
                          )}
                        </div>

                        <Separator />

                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Stage</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[120px]">
                              <div
                                className="bg-primary h-2 rounded-full transition-all"
                                style={{ width: `${(currentData.strategyPhase.currentPhase / currentData.strategyPhase.totalPhases) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {currentData.strategyPhase.currentPhase}/{currentData.strategyPhase.totalPhases}
                            </span>
                          </div>
                          <p className="text-sm font-medium">{currentData.strategyPhase.phaseName}</p>
                        </div>
                      </div>

                      {/* Second Column */}
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            Outstanding Amount
                          </p>
                          <p className="text-sm font-semibold">{formatCurrency(currentData.amount)}</p>
                        </div>

                        <Separator />

                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Days in Collection
                          </p>
                          <p className="text-sm font-semibold">{currentData.daysInCollection} days</p>
                        </div>

                        <Separator />

                        <div className="space-y-1">
                          <Label htmlFor="status" className="text-xs font-medium text-muted-foreground">
                            Case Status
                          </Label>
                          {isEditMode && editedData ? (
                            <Select
                              value={editedData.status}
                              onValueChange={(value: 'Active' | 'On Hold' | 'Closed') => 
                                setEditedData({ ...editedData, status: value })
                              }
                            >
                              <SelectTrigger id="status">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Promised">Promised</SelectItem>
                                <SelectItem value="Escalated">Escalated</SelectItem>
                                <SelectItem value="On Hold">On Hold</SelectItem>
                                <SelectItem value="Closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={cn("text-sm", getStatusBadgeColor(currentData.status))}>
                              {currentData.status}
                            </Badge>
                          )}
                        </div>

                        <Separator />

                        <div className="space-y-1">
                          <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground">
                            Priority Level
                          </Label>
                          {isEditMode && editedData ? (
                            <Select
                              value={editedData.priority}
                              onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                                setEditedData({ ...editedData, priority: value })
                              }
                            >
                              <SelectTrigger id="priority">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="High">High</SelectItem>
                                <SelectItem value="Medium">Medium</SelectItem>
                                <SelectItem value="Low">Low</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant={getPriorityBadgeVariant(currentData.priority)} className="text-sm">
                              {currentData.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Next Action - Call to Action Style */}
              <Card className="border-2 border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Next Scheduled Action
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="font-semibold text-lg">{caseData.nextAction}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>Due: {format(new Date(caseData.nextActionDate), "MMMM d, yyyy")}</span>
                      </div>
                    </div>
                    <Button 
                      size="lg" 
                      className="gap-2"
                      onClick={() => {
                        // Check if the next action is "Send final notice"
                        if (caseData.nextAction === "Send final notice") {
                          setShowSendFinalNoticeDialog(true);
                        } else {
                          setShowCompleteActionDialog(true);
                        }
                      }}
                    >
                      <Activity className="h-4 w-4" />
                      Complete Next Action
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Promise to Pay Section - Only show if status is Promised */}
              {caseData.status === 'Promised' && caseData.currentPromise && (
                <Card className="border-2 border-blue-200 bg-blue-50 dark:border-blue-900/30 dark:bg-blue-950/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Active Promise to Pay
                    </CardTitle>
                    <CardDescription>Customer payment commitment details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Promise Date</p>
                          <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                            {format(new Date(caseData.currentPromise.promiseDate), "MMMM d, yyyy")}
                          </p>
                        </div>
                        <Separator />
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Promised Amount</p>
                          <p className="text-lg font-semibold">£{caseData.currentPromise.promisedAmount.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Confirmation Method</p>
                          <Badge variant="outline" className="text-sm">{caseData.currentPromise.confirmationMethod}</Badge>
                        </div>
                        <Separator />
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Created By</p>
                          <p className="text-sm">{caseData.currentPromise.createdBy}</p>
                        </div>
                        {caseData.brokenPromiseCount && caseData.brokenPromiseCount > 0 && (
                          <>
                            <Separator />
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Promise History</p>
                              <Badge variant="destructive" className="text-sm">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {caseData.brokenPromiseCount} broken promise(s)
                              </Badge>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {caseData.currentPromise.notes && (
                      <>
                        <Separator className="my-4" />
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Notes</p>
                          <p className="text-sm">{caseData.currentPromise.notes}</p>
                        </div>
                      </>
                    )}
                    <Separator className="my-4" />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="default"
                        className="flex-1"
                        onClick={() => setShowMarkFulfilledModal(true)}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Mark as Paid
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        className="flex-1"
                        onClick={() => setShowMarkBrokenModal(true)}
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Mark as Broken
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Notes Section - Collapsible */}
              {(currentData.notes || isEditMode) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      Case Notes
                    </CardTitle>
                    <CardDescription>
                      {isEditMode ? "Edit case notes or view full history in the Notes History tab" : "Quick view of case notes"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {isEditMode && editedData ? (
                      <Textarea
                        id="notes"
                        value={editedData.notes || ''}
                        onChange={(e) => setEditedData({ ...editedData, notes: e.target.value })}
                        placeholder="Add notes about this case..."
                        rows={4}
                        className="resize-none"
                      />
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm leading-relaxed">{currentData.notes}</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setActiveTab("notes")}
                          className="gap-2"
                        >
                          <History className="h-4 w-4" />
                          View Full Notes History
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="transactions" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Outstanding Transactions</CardTitle>
                      <CardDescription>
                        {isEditMode 
                          ? `Select transactions to include in this case (${selectedTransactions.size} selected)`
                          : "All transactions associated with this collection case"
                        }
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {isEditMode && <TableHead className="w-12"></TableHead>}
                        <TableHead>Reference Number</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Days Overdue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mockTransactions.map((transaction) => (
                        <TableRow 
                          key={transaction.id}
                          className={cn(
                            isEditMode && "cursor-pointer hover:bg-muted/50",
                            isEditMode && selectedTransactions.has(transaction.id) && "bg-muted/30"
                          )}
                          onClick={() => isEditMode && handleToggleTransaction(transaction.id)}
                        >
                          {isEditMode && (
                            <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedTransactions.has(transaction.id)}
                                onCheckedChange={() => handleToggleTransaction(transaction.id)}
                                aria-label={`Select ${transaction.invoiceNumber}`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-medium">
                            {transaction.invoiceNumber}
                          </TableCell>
                          <TableCell>
                            {format(transaction.invoiceDate, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell>
                            {format(transaction.dueDate, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell>
                            <TransactionTypeBadge type={transaction.type} />
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(transaction.amount)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(transaction.balance)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                transaction.status === "Overdue"
                                  ? "destructive"
                                  : "secondary"
                              }
                            >
                              {transaction.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {transaction.daysOverdue > 0 ? (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                {transaction.daysOverdue}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Outstanding:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(caseData.amount)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="space-y-4">
              <ConsolidatedActivityTimeline
                activities={mockActivities}
                notes={notesHistory}
                communications={mockCommunications}
                isEditMode={isEditMode}
                onAddNote={async (content) => {
                  const newNote: CaseNote = {
                    id: `note-${Date.now()}`,
                    caseId: caseData.caseId,
                    content: content,
                    createdBy: "Current User",
                    createdAt: new Date(),
                  };
                  setNotesHistory([newNote, ...notesHistory]);
                  toast.success("Note added successfully");
                }}
              />
            </TabsContent>

            <TabsContent value="audit" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Complete chronological history of all case modifications and changes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {mockAuditLogs.map((log, index) => {
                      const Icon = getAuditLogIcon(log.action);
                      const colorClass = getAuditLogColor(log.action);
                      
                      return (
                        <div key={log.id} className="relative">
                          {index !== mockAuditLogs.length - 1 && (
                            <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                          )}
                          <div className="flex gap-4">
                            <div className={cn(
                              "flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0",
                              colorClass
                            )}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 space-y-1">
                                  <p className="font-medium text-sm">{log.description}</p>
                                  
                                  {/* Status/Priority/Field Changes */}
                                  {(log.action === "status_change" || log.action === "priority_change" || log.action === "field_update" || log.action === "assignment_change") && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <Badge variant="outline" className="text-xs">
                                        {log.oldValue || "None"}
                                      </Badge>
                                      <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                      <Badge variant="outline" className="text-xs">
                                        {log.newValue}
                                      </Badge>
                                    </div>
                                  )}
                                  
                                  {/* Transaction Added */}
                                  {log.action === "transaction_added" && log.transactionNumber && (
                                    <div className="text-sm text-muted-foreground space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{log.transactionNumber}</span>
                                        <span>•</span>
                                        <span className="font-medium text-green-600 dark:text-green-400">
                                          {log.transactionAmount ? formatCurrency(log.transactionAmount) : '-'}
                                        </span>
                                      </div>
                                      {log.transactionDate && (
                                        <div className="text-xs">
                                          Invoice Date: {format(log.transactionDate, "MMM d, yyyy")}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Transaction Removed */}
                                  {log.action === "transaction_removed" && log.transactionNumber && (
                                    <div className="text-sm text-muted-foreground space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{log.transactionNumber}</span>
                                        <span>•</span>
                                        <span className="font-medium text-red-600 dark:text-red-400">
                                          {log.transactionAmount ? formatCurrency(log.transactionAmount) : '-'}
                                        </span>
                                      </div>
                                      {log.transactionDate && (
                                        <div className="text-xs">
                                          Invoice Date: {format(log.transactionDate, "MMM d, yyyy")}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground whitespace-nowrap">
                                  {format(log.createdAt, "MMM d, yyyy h:mm a")}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  <User className="h-3 w-3 mr-1" />
                                  {log.createdBy}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {mockAuditLogs.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No audit log entries found for this case.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </Tabs>
      </SheetContent>

      {/* Promise Management Modals */}
      {caseData.currentPromise && (
        <>
          <MarkPromiseFulfilledModal
            open={showMarkFulfilledModal}
            onOpenChange={setShowMarkFulfilledModal}
            customerName={caseData.customerName}
            promiseAmount={caseData.currentPromise.promisedAmount}
            promiseDate={new Date(caseData.currentPromise.promiseDate)}
            onSave={(data) => {
              console.log("Promise fulfilled:", data);
              // TODO: Update case status and record payment
              setShowMarkFulfilledModal(false);
            }}
          />
          <MarkPromiseBrokenModal
            open={showMarkBrokenModal}
            onOpenChange={setShowMarkBrokenModal}
            customerName={caseData.customerName}
            promiseAmount={caseData.currentPromise.promisedAmount}
            promiseDate={new Date(caseData.currentPromise.promiseDate)}
            onSave={(data) => {
              console.log("Promise broken:", data);
              // TODO: Update case status and increment broken promise count
              setShowMarkBrokenModal(false);
            }}
          />
        </>
      )}

      {/* Complete Action Dialog */}
      <CompleteActionDialog
        open={showCompleteActionDialog}
        onOpenChange={setShowCompleteActionDialog}
        currentAction={{
          action: caseData.nextAction,
          dueDate: caseData.nextActionDate.toString(),
        }}
        caseId={caseData.caseId}
        onComplete={handleCompleteAction}
      />

      {/* Send Final Notice Dialog */}
      <SendFinalNoticeDialog
        open={showSendFinalNoticeDialog}
        onOpenChange={setShowSendFinalNoticeDialog}
        caseData={{
          customerName: caseData.customerName,
          accountNumber: caseData.customerId,
          totalAmountDue: caseData.amount,
          overdueAmount: caseData.amount,
          daysPastDue: caseData.daysInCollection,
          invoiceNumbers: mockTransactions.map(t => t.invoiceNumber),
          recipientEmail: caseData.customerEmail,
        }}
        onSendEmail={async () => {
          // Simulate sending email
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Show success message
          toast.success("Final notice sent successfully", {
            description: `Email sent to ${caseData.customerEmail}`,
          });
          
          // In a real implementation, this would:
          // 1. Send the email via API
          // 2. Update the case with the completion data
          // 3. Add notes to the case history
          // 4. Set the next action if required
          // 5. Refresh the case data
          
          console.log("Final notice email sent to:", caseData.customerEmail);
        }}
      />
    </Sheet>
  );
}
